import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IWindowService } from 'angular';
import { Subscription } from 'rxjs';
import { WINDOW_INJECTION_TOKEN } from 'src/app/injection-tokens';
import { DOW } from 'src/shared/models/module-manager/module-manager-settings.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { DateRangeSelectorService } from '../date-range-selector/date-range-selector.service';
import { CvConfigurationService } from '../services/cv-configuration.service';
import { CVLocalizationService } from '../services/cv-localization.service';


@Component({
    selector: 'cv-dow-selector',
    templateUrl: './dow-selector.component.html',
    styleUrls: ['./dow-selector.component.scss']
})
export class DowSelectorComponent implements OnInit, OnChanges {
    @Input() pageName: string;
    @Input() selectedDaysOfWeek: DOW = 127 as DOW;
    @Input() settingsManager?: SlideoutSettingsManager;
    @Input() displayMode: 'button' | 'checkbox' = 'button';
    @Input() size: 'small' | 'medium' | 'large' = 'small';
    @Output() selectedDaysOfWeekChange = new EventEmitter<DOW>();

    readonly days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    daysInOrder: string[] = [];
    daysLocalizedInOrder: string[] = [];

    checkedDays = {
        /* eslint-disable no-bitwise */
        getDay: (day: DOW) => (this.selectedDaysOfWeek | day) === this.selectedDaysOfWeek,
        setDay: (day: DOW, enabled: boolean) => {
            if (enabled)
                this.selectedDaysOfWeek |= day;
            else
                this.selectedDaysOfWeek &= ~day;
            this.selectedDaysOfWeekChange.emit(this.selectedDaysOfWeek);
        },
        /* eslint-enable no-bitwise */

        get Sun() { return this.getDay(DOW.Sunday); },
        set Sun(v) { this.setDay(DOW.Sunday, v); },

        get Mon() { return this.getDay(DOW.Monday); },
        set Mon(v) { this.setDay(DOW.Monday, v); },

        get Tue() { return this.getDay(DOW.Tuesday); },
        set Tue(v) { this.setDay(DOW.Tuesday, v); },

        get Wed() { return this.getDay(DOW.Wednesday); },
        set Wed(v) { this.setDay(DOW.Wednesday, v); },

        get Thu() { return this.getDay(DOW.Thursday); },
        set Thu(v) { this.setDay(DOW.Thursday, v); },

        get Fri() {return this.getDay(DOW.Friday); },
        set Fri(v) { this.setDay(DOW.Friday, v); },

        get Sat() { return this.getDay(DOW.Saturday); },
        set Sat(v) { this.setDay(DOW.Saturday, v); },
    };

    get useCxOne() { return this.config.isCxOne; }

    _saveDebounce: number;
    private _pendingSave: Promise<void> = null;
    private _pendingSaveResolve: () => void = null;
    private _subscriptions: Subscription[] = [];

    constructor(
        @Inject(WINDOW_INJECTION_TOKEN) private window: Window,
        private config: CvConfigurationService,
        private service: DateRangeSelectorService,
        private cvLocalizationService: CVLocalizationService
    ) {

    }

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Dashboards_Landing");

        const weekStart: number = this.config.weekStart;
        this.daysInOrder = [];
        for (let i = 0; i < 7; i++) {
            const currDayNumber = (weekStart + i) % 7;
            const currDay = this.days[currDayNumber].slice(0, 3);
            const currDayLocalized = this.cvLocalizationService.getCachedText("Dashboards_Landing",
                this.days[currDayNumber], this.days[currDayNumber]).slice(0, 3);
            this.daysInOrder.push(currDay);
            this.daysLocalizedInOrder.push(currDayLocalized);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settingsManager?.currentValue) {
            const beforeApplySubscription = this.settingsManager.beforeSettingsApplied.subscribe(() => {
                if (this._pendingSave != null) {
                    this.settingsManager.waitBeforeApplying.push(this._pendingSave);
                }
            });
            this._subscriptions.push(beforeApplySubscription);
        }
    }

    onDaysChanged() {
        if (this._saveDebounce != null)
            clearTimeout(this._saveDebounce);

        if (this._pendingSave == null)
            this._pendingSave = new Promise(resolve => this._pendingSaveResolve = resolve);

        this._saveDebounce = this.window.setTimeout(() => {
            this._saveDebounce = null;
            this.service.saveDaysOfWeek(this.pageName, this.selectedDaysOfWeek).finally(() => {
                this._pendingSaveResolve?.();
                this._pendingSave = null;
                this._pendingSaveResolve = null;
            });
        }, 500);
    }
}
